import * as React from 'react';
import {
    ContextActionType,
    ModalContext,
    useDumbActionsContext,
} from '@europrocurement/flexy-components';

import {
    ColumnDatatable,
    FilterDatatable,
    FilterOptionValue,
    FiltersDatatableList,
} from '@europrocurement/flexy-datatable';
import { useDispatch } from 'react-redux';

import { CatalogueJsonldCatalogueRead } from '@europrocurement/l2d-domain/openApi/ApiOffre';
import { UnknownAction } from 'redux';
import { addItem, FaOptionIcon } from '@europrocurement/l2d-icons';
import { useOffreService } from '@europrocurement/l2d-domain';
import { useDefaultContextActions } from '@europrocurement/l2d-modules/hooks';
import { Typography } from '@mui/material';
import ListingWithSearchAndPreview from '../components/ListingWithSearchAndPreview';
import Form from './Form/Form';

import { displayCustomValue, getStringColumn } from '../listings';
import useFilterOptions from './useFilterOptions';
import { PossibleTypes } from '../../Societes';

const Panel: React.FunctionComponent = function () {
    const { modalActions } = React.useContext(ModalContext);

    const { catalogModel: model } = useOffreService();
    const { backContextActions } = useDefaultContextActions();
    const filterOptions = useFilterOptions();

    const dispatch = useDispatch();

    const reloadList = () =>
        dispatch(model.datasourcesThunks.main.getData({}) as unknown as UnknownAction);

    const listingButton = {
        label: 'Créer un nouveau catalogue',
        icon: addItem,
        action: () =>
            modalActions.call(
                <Form
                    isCreate
                    afterSubmit={reloadList}
                />,
            ),
    };

    const columns: ColumnDatatable<CatalogueJsonldCatalogueRead>[] = [
        {
            ...getStringColumn('id'),

            sortable: true,
        },
        {
            ...getStringColumn('nomCommercial', undefined, 60),
            sortable: true,
        },
        {
            label: 'Nom du package associé',
            displayLabel: true,
            render: (item) => {
                const cataloguePackageFormality = item?.package as { nomCommercial?: string };

                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {cataloguePackageFormality?.nomCommercial ?? '(aucun package)'}
                    </Typography>
                );
            },
            isDisplayed: true,
            sortable: false,
            field: 'package',
        },
        {
            ...getStringColumn('dateDebut', 'Début'),
            sortable: true,
        },
        {
            ...getStringColumn('dateFin', 'Fin'),
            sortable: true,
        },
        {
            ...displayCustomValue('groupement', (group) => group.libelle, 'Groupement', 14),
            sortable: true,
        },
        {
            ...displayCustomValue('offre', (offer) => offer.libelle, 'Offre'),
            sortable: true,
        },
        {
            label: 'Domaine',
            displayLabel: true,
            render: (item) => {
                const domain = item.domaine as { libelle?: string };
                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {domain?.libelle ?? '(aucun domaine)'}
                    </Typography>
                );
            },
            isDisplayed: true,
            sortable: false,
            field: 'domaine',
        },
        {
            label: 'Origine',
            displayLabel: true,
            render: (item) => {
                const origine = item.origine as { origine?: string };

                return (
                    <Typography
                        variant="l2dtableCell"
                        color="text.gris70"
                    >
                        {origine?.origine ?? '(aucune origine)'}
                    </Typography>
                );
            },
            isDisplayed: true,
            sortable: false,
            field: 'origine',
        },
    ];

    const contextActions: ContextActionType[] = React.useMemo(
        () => [
            {
                name: 'Créer un nouveau catalogue',
                icon: <FaOptionIcon icon={['fas', 'plus-circle']} />,
                action: listingButton.action,
            },
            ...backContextActions,
        ],
        [backContextActions, listingButton.action],
    );

    useDumbActionsContext(contextActions);

    /** Generic function : Generate filter object */
    const getFilter = (
        label: string,
        field: string,
        options: FilterOptionValue[],
    ): FilterDatatable => ({
        label,
        field,
        type: 'staticSelect' as const,
        options,
        renderFilterList: (value: string) => {
            const selectedOption = options.find((option) => option.value === value);
            return selectedOption ? selectedOption.label : 'Aucun';
        },
    });

    const filters: FiltersDatatableList = [
        {
            field: 'nomCommercial',
            label: 'Nom commercial',
            type: 'text',
        },
        {
            field: 'packageNomCommercial',
            label: 'Nom du package associé',
            type: 'text',
        },
        getFilter('Domaine', 'domaineId', filterOptions.domains),
        getFilter('Offre', 'offreId', filterOptions.offers),
        getFilter('Groupement', 'groupementId', filterOptions.groups),
        getFilter('Origine', 'origineId', filterOptions.origins),
    ];

    return (
        <ListingWithSearchAndPreview
            listingTitle="Liste des catalogues"
            listingButton={listingButton}
            listingColumns={columns}
            form={Form}
            model={model}
            filters={filters}
            filtersControl
            searchBarType={PossibleTypes.Catalogue}
            hidePreview
        />
    );
};

export default Panel;
